import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid, parse } from "date-fns";
import enGB from "date-fns/locale/en-GB";
import { useRef } from "react";
import TextFieldComponent from "./TextFieldComponent";
import { TimePicker } from "@mui/x-date-pickers";

const TimePickerComponent = ({
  value: selected,
  name,
  label,
  onChange,
  error,
  isMandatory = false,
}) => {
  const getHourMinute = (raw) => {
    if (typeof raw !== "string") return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;

    // MUI can display placeholders like "11:mm" while typing; treat as hour-only.
    const hourWithPlaceholder = trimmed.match(/^(\d{1,2}):(?:mm|MM|__|··)$/);
    if (hourWithPlaceholder) {
      const h = Number(hourWithPlaceholder[1]);
      if (!Number.isInteger(h) || h < 0) return null;
      return { h, m: 0, kind: "hourOnly", rawHourLen: hourWithPlaceholder[1].length };
    }

    const hourOnly = trimmed.match(/^(\d{1,2})$/);
    if (hourOnly) {
      const h = Number(hourOnly[1]);
      if (!Number.isInteger(h) || h < 0) return null;
      return { h, m: 0, kind: "hourOnly", rawHourLen: hourOnly[1].length };
    }

    const hm = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (hm) {
      const h = Number(hm[1]);
      const m = Number(hm[2]);
      if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || m < 0 || m > 59) return null;
      return { h, m, kind: "hm", rawHourLen: hm[1].length };
    }

    return null;
  };

  const toDisplayString = (parts) => {
    if (!parts) return null;
    const hh = parts.rawHourLen === 1 ? String(parts.h).padStart(2, "0") : String(parts.h);
    const mm = String(parts.m).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const normalizeTimeString = (raw) => {
    const parts = getHourMinute(raw);
    if (!parts) return null;
    // Per requirement: allow hour-only like "35" => "35:00" (do not let picker reinterpret it)
    return toDisplayString(parts);
  };

  const normalizedSelected =
    typeof selected === "string" ? normalizeTimeString(selected) ?? selected : selected;

  // Parse string back to valid Date object
  const parsedParts =
    typeof normalizedSelected === "string" ? getHourMinute(normalizedSelected) : null;
  const parsedDate =
    parsedParts && parsedParts.h >= 0 && parsedParts.h <= 23
      ? parse(toDisplayString(parsedParts), "HH:mm", new Date())
      : selected instanceof Date
      ? selected
      : null;

  const lastTypedRef = useRef("");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <TimePicker
        ampm={false}
        label={
          <span>
            {label}
            {isMandatory && <span className="text-error"> *</span>}
          </span>
        }
        value={parsedDate}
        onChange={newValue => {
          if (newValue && isValid(newValue)) {
            const raw = String(lastTypedRef.current || "").trim();

            // If user typed hour-only, always normalize to "<typed>:00" (e.g. 35 -> 35:00)
            const rawParts = getHourMinute(raw);
            if (rawParts?.kind === "hourOnly") {
              onChange({ target: { name, value: toDisplayString(rawParts) } });
              return;
            }

            onChange({ target: { name, value: format(newValue, "HH:mm") } });
          } else {
            onChange({ target: { name, value: "" } });
          }
        }}
        format="HH:mm"
        slotProps={{
          textField: {
            error: !!error,
            helperText: error,
            onChange: (e) => {
              const raw = e?.target?.value ?? "";
              lastTypedRef.current = raw;

              // If user typed hour-only (including placeholder forms like "11:mm"),
              // immediately commit "<hour>:00" to avoid leaving the minutes section as "mm".
              const parts = getHourMinute(raw);
              if (parts?.kind === "hourOnly") {
                onChange({ target: { name, value: toDisplayString(parts) } });
              }
            },
            onInput: (e) => {
              lastTypedRef.current = e?.target?.value ?? "";
            },
            onFocus: () => {
              // Match DatePicker behavior: if empty, set current time on focus/click.
              if (!selected) {
                onChange({ target: { name, value: format(new Date(), "HH:mm") } });
              }
            },
            onBlur: (e) => {
              const next = normalizeTimeString(e?.target?.value);
              if (next) onChange({ target: { name, value: next } });
            },
            onKeyDown: (e) => {
              if (e?.key !== "Enter") return;
              const next = normalizeTimeString(e?.target?.value);
              if (next) onChange({ target: { name, value: next } });
            },
          },
          layout: {
            sx: {
              "& .MuiDialogActions-root": {
                justifyContent: "center",
                padding: "12px 16px",
                gap: "8px",
              },
        
              "& .MuiDialogActions-root .MuiButton-root": {
                minWidth: "80px",
                textTransform: "none",
                fontWeight: 500,
              },
            },
          },
          desktopPaper: {
            sx: {
              "& .MuiMultiSectionDigitalClock-root": {
                width: "190px",
                gap: "8px",
              },
              "& .MuiMultiSectionDigitalClockSection-root": {
                width: "80px",
                minWidth: "80px",
                maxWidth: "80px",
                scrollbarWidth: "thin",
              },
              "& .MuiMenuItem-root": {
                justifyContent: "center",
                fontSize: "15px",
                minWidth: "70px",
              },
            },
          },
        }}
        sx={{
          "& .MuiFormLabel-root": {
            position: "static",
            color: "#44475a !important",
          },
          "& .MuiInputBase-input": {
            borderTop: "1px solid #eaeadc",
          },
          "& .MuiButtonBase-root": {
            margin: "0",
          },
        }}
        renderInput={params => <TextFieldComponent {...params} />}
      />
    </LocalizationProvider>
  );
};

export default TimePickerComponent;
